import type {
  CreateTimesheetInput,
  PatchTimesheetInput,
  Timesheet,
} from '@clockwise/clients/timesheets';
import type { User } from '@clockwise/clients/users';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { timesheetsApi } from '@/lib/api';
import { engagementRequiredCandidates } from '@/lib/engagement-required';
import { queryKeys } from '@/lib/query-keys';
import { roleRequiredCandidates } from '@/lib/role-required';
import { useEngagementRequiredPrompt } from './engagement-required-prompt';
import { RoleDisambiguationDialog } from './role-disambiguation-dialog';
import { addDays, newOptimisticId, optimisticTimesheet } from './timesheet-week';

/** A create that's been optimistically applied but whose POST hasn't returned a
 *  real id yet. Patches/deletes against the temp id must wait on `promise` (the
 *  server has no such row, so they'd 404 and roll back — resurrecting the
 *  pre-edit row). Resolves with the real id on create success, rejects on
 *  create failure (the optimistic row is rolled back, so queued ops are dropped). */
interface PendingCreate {
  promise: Promise<string>;
  resolve: (realId: string) => void;
  reject: (err: unknown) => void;
}

interface RolePromptState {
  candidateRoleIds: string[];
  onPick: (roleId: string) => void;
  onCancel: () => void;
}

/** Merge a PATCH payload into an in-cache Timesheet for optimistic
 *  display. Treats `undefined` as "leave alone" and `null` as "clear",
 *  matching the backend's `=== undefined` vs `=== null` distinction.
 *
 *  Note: legacy `serviceId` / `serviceVersion` on the entry are
 *  intentionally NOT touched here — the patch shape doesn't carry them
 *  and the server is the only writer (and only nulls them via migration).
 *  Row grouping in the grid keys on `productId` alone; any stale
 *  `serviceId` on the cached entry is purely cosmetic data the timesheet
 *  UI does not act on. Pure — easy to unit-test. */
export function mergePatch(entry: Timesheet, patch: PatchTimesheetInput): Timesheet {
  return {
    ...entry,
    customerId: patch.customerId !== undefined ? (patch.customerId ?? null) : entry.customerId,
    productId: patch.productId !== undefined ? (patch.productId ?? null) : entry.productId,
    // Every field the patch can carry has to be listed here. A field that is
    // missing is silently dropped from the OPTIMISTIC row, so the UI ignores
    // the user's edit until the server round trip lands and `onSuccess`
    // overwrites the row — which reads as "my pick didn't take", and as taking
    // effect only on the second attempt.
    engagementId:
      patch.engagementId !== undefined ? (patch.engagementId ?? null) : entry.engagementId,
    hours:
      patch.hours !== undefined ? (patch.hours == null ? null : String(patch.hours)) : entry.hours,
    description: patch.description !== undefined ? (patch.description ?? null) : entry.description,
    billable: patch.billable !== undefined ? patch.billable : entry.billable,
    updatedAt: new Date().toISOString(),
  };
}

type RangeCache = { data: Timesheet[] } | undefined;

/** A refusal the save wrappers answer with a picker and a retry, not a toast. */
const isPromptable = (err: unknown): boolean =>
  Boolean(roleRequiredCandidates(err)?.length || engagementRequiredCandidates(err)?.length);

/**
 * Centralizes the optimistic create/patch/delete mutations used by both
 * the week grid and the day list. The shape (snapshot + setQueryData +
 * onError rollback) is delicate; keeping it in one place stops the two
 * views from drifting apart. Submission is NOT here — it lives only on
 * the week view as a page-level inline mutation (one transaction per
 * week).
 *
 * Error toasts are owned by this hook so all call sites get the same
 * UX. Success toasts live with the caller — they're context-specific.
 */
export function useTimesheetMutations(user: User, weekStart: string) {
  const queryClient = useQueryClient();
  // Set when a save needs the user to choose a customer-team role (see
  // `roleRequiredCandidates`). The dialog host is returned below; both the
  // week grid and the day list render it.
  const [rolePrompt, setRolePrompt] = useState<RolePromptState | null>(null);
  // Set when a save is refused because several engagements cover the entry and
  // none was chosen (ENGAGEMENT_REQUIRED) — same ask-and-retry shape as the
  // role prompt. See `useEngagementRequiredPrompt`.
  const engagementPrompt = useEngagementRequiredPrompt();
  const rangeKey = queryKeys.timesheets.range({
    userId: user.id,
    fromDate: weekStart,
    toDate: addDays(weekStart, 6),
  });

  const setRange = (updater: (prev: Timesheet[]) => Timesheet[]) =>
    queryClient.setQueryData<RangeCache>(rangeKey, (prev) =>
      prev ? { ...prev, data: updater(prev.data) } : { data: updater([]) },
    );
  const snapshotAndCancel = async (): Promise<RangeCache> => {
    await queryClient.cancelQueries({ queryKey: rangeKey });
    return queryClient.getQueryData<RangeCache>(rangeKey);
  };

  // Optimistic creates in flight, keyed by their `temp-…` id. A patch/delete
  // the user fires against a row whose create hasn't returned yet must target
  // the REAL id, not the temp id — so it waits on the create here and replays
  // once the id is known. Without this the request 404s and its rollback (plus
  // the create's own onSuccess) brings the pre-edit hours back.
  const pendingCreates = useRef(new Map<string, PendingCreate>());

  const createMutation = useMutation({
    mutationFn: ({ input }: { input: CreateTimesheetInput; tempId: string }) =>
      timesheetsApi.create({ userId: user.id, ...input }),
    onMutate: async ({ input, tempId }: { input: CreateTimesheetInput; tempId: string }) => {
      const previous = await snapshotAndCancel();
      // The temp id is minted by `createEntry` (not here) so the caller can
      // claim the optimistic row synchronously — the week grid uses it to keep
      // a manually-added duplicate row separate the instant the entry is added.
      // Register the create so a patch/delete on this temp id can await the
      // real id (see patchEntry / deleteEntry). Settled in onSuccess/onError below.
      let resolve!: (realId: string) => void;
      let reject!: (err: unknown) => void;
      const promise = new Promise<string>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      // Nothing may ever await a failed create — swallow so it isn't an
      // unhandled rejection.
      promise.catch(() => {});
      pendingCreates.current.set(tempId, { promise, resolve, reject });
      setRange((data) => [
        ...data,
        optimisticTimesheet(tempId, user.id, {
          customerId: input.customerId ?? null,
          productId: input.productId ?? null,
          // Part of the row key — an optimistic row without it would render in
          // a different row than the one the server's reply lands in.
          engagementId: input.engagementId ?? null,
          date: input.date,
          hours: input.hours ?? null,
          description: input.description ?? null,
          billable: input.billable ?? false,
        }),
      ]);
      return { previous, tempId };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(rangeKey, ctx.previous);
      // The optimistic row is gone — reject so any queued patch/delete on its
      // temp id drops instead of firing against a row that never existed.
      if (ctx?.tempId) {
        pendingCreates.current.get(ctx.tempId)?.reject(err);
        pendingCreates.current.delete(ctx.tempId);
      }
      // ROLE_REQUIRED / ENGAGEMENT_REQUIRED are handled by createEntry/patchEntry
      // (they open a picker and retry) — don't toast them as hard failures.
      if (isPromptable(err)) return;
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
    onSuccess: (res, _input, ctx) => {
      // Defensive: if onMutate threw before producing a tempId, fall back
      // to merging by the real id (or appending). Without this, a missing
      // tempId would leave the optimistic row in cache forever.
      const tempId = ctx?.tempId;
      setRange((data) => {
        if (tempId && data.some((e) => e.id === tempId)) {
          return data.map((e) => (e.id === tempId ? res.data : e));
        }
        const exists = data.some((e) => e.id === res.data.id);
        return exists
          ? data.map((e) => (e.id === res.data.id ? res.data : e))
          : [...data, res.data];
      });
      // Unblock any patch/delete queued against the temp id — they replay now
      // with the real id (see patchEntry / deleteEntry).
      if (tempId) {
        pendingCreates.current.get(tempId)?.resolve(res.data.id);
        pendingCreates.current.delete(tempId);
      }
    },
  });

  const patchMutation = useMutation({
    mutationFn: (input: { id: string; patch: PatchTimesheetInput }) =>
      timesheetsApi.patch(input.id, input.patch),
    onMutate: async ({ id, patch }) => {
      const previous = await snapshotAndCancel();
      setRange((data) => data.map((e) => (e.id === id ? mergePatch(e, patch) : e)));
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(rangeKey, ctx.previous);
      if (isPromptable(err)) return;
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
    onSuccess: (res) => {
      setRange((data) => data.map((e) => (e.id === res.data.id ? res.data : e)));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => timesheetsApi.remove(id),
    onMutate: async (id) => {
      const previous = await snapshotAndCancel();
      setRange((data) => data.filter((e) => e.id !== id));
      return { previous };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(rangeKey, ctx.previous);
      // A row action can dispatch several DELETEs together. If the period
      // locks after render, every request can fail with the same response;
      // give those failures one stable toast instead of stacking duplicates.
      // Keyed by MESSAGE, not a bare constant: sonner updates in place on a
      // repeated id, so a single global id would let a second, DIFFERENT
      // failure in the same batch (locked period vs off-team) overwrite the
      // first and leave the user one error short of the truth.
      const message = err instanceof Error ? err.message : 'Delete failed';
      toast.error(message, { id: `timesheet-delete-error:${message}` });
    },
  });

  // Recall a single SUBMITTED entry back to draft. Distinct from the
  // week-level recall (which recalls the whole week from the header): this is
  // a per-entry action on a submitted cell/card. Approved entries can't be
  // recalled by the submitter. Optimistically flips the row to draft so it
  // unlocks immediately; the success toast is owned here because recall is a
  // discrete user action (unlike autosaves).
  const recallEntryMutation = useMutation({
    mutationFn: (id: string) => timesheetsApi.recallEntry(id),
    onMutate: async (id) => {
      const previous = await snapshotAndCancel();
      setRange((data) =>
        data.map((e) =>
          e.id === id
            ? { ...e, status: 'draft', resolutionReason: null, updatedAt: new Date().toISOString() }
            : e,
        ),
      );
      return { previous };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(rangeKey, ctx.previous);
      toast.error(err instanceof Error ? err.message : 'Recall failed');
    },
    onSuccess: (res) => {
      setRange((data) => data.map((e) => (e.id === res.data.id ? res.data : e)));
      toast.success('Entry recalled to draft. Edit and resubmit for approval.');
    },
  });

  // Save wrappers that add disambiguation on top of the optimistic mutations.
  // On the common path they behave exactly like `.mutate`. When the server
  // can't resolve the customer-team role (ROLE_REQUIRED — the activity is
  // loggable under >1 of the user's roles), they open the role picker and
  // re-run the SAME save with the chosen `customerTeamServiceRoleId` merged in;
  // when several engagements cover the entry and none was chosen
  // (ENGAGEMENT_REQUIRED), the same with the chosen `engagementId`.
  // Call sites use these instead of `createMutation.mutate` /
  // `patchMutation.mutate` (the raw mutations remain exposed for `isPending`).
  const createEntry = (
    input: CreateTimesheetInput,
    opts?: {
      /** Called (with the failed attempt's temp id) on a terminal create
       *  failure or when the user abandons a ROLE_REQUIRED prompt, so the
       *  caller can release any claim it staked on the optimistic row. */
      onError?: (tempId: string) => void;
      /** Called synchronously with the freshly-minted temp id BEFORE the POST,
       *  so the caller can claim the optimistic row without a render of flicker. */
      onOptimisticId?: (tempId: string) => void;
      /** Called once the POST returns, mapping the temp id to its real id so
       *  the caller can re-point its claim at the persisted row. */
      onRealId?: (tempId: string, realId: string) => void;
    },
  ) => {
    // Mint the temp id here (not in onMutate) so `onOptimisticId` fires before
    // the mutation runs — the grid claims the row on the same tick it's added.
    const tempId = newOptimisticId();
    opts?.onOptimisticId?.(tempId);
    // `mutateAsync`, not `mutate(_, callbacks)`: React Query keeps only the
    // LATEST `mutate` call's per-call callbacks (see `deleteEntry`), so two
    // creates in flight at once would lose the first one's retry prompt.
    createMutation.mutateAsync({ input, tempId }).then(
      (res) => opts?.onRealId?.(tempId, res.data.id),
      (err) => {
        // Refused for want of an engagement — ask, then retry the same create
        // with the pick. The optimistic row was rolled back; the retry mints a
        // new temp id, so this attempt's claim is released only on cancel.
        const engagements = engagementRequiredCandidates(err);
        if (engagements && engagements.length > 0) {
          engagementPrompt.ask({
            candidates: engagements,
            customerId: input.customerId ?? null,
            productId: input.productId ?? null,
            onPick: (engagementId) => createEntry({ ...input, engagementId }, opts),
            onCancel: () => opts?.onError?.(tempId),
          });
          return;
        }
        const candidates = roleRequiredCandidates(err);
        if (!candidates || candidates.length === 0) {
          // Not a usable ROLE_REQUIRED prompt (not that error, or a malformed
          // payload with no candidates) — hard failure. Let the caller release
          // its claim; the mutation's own onError already reverted + toasted.
          // (Don't open the picker with zero options — that's a dead end.)
          opts?.onError?.(tempId);
          return;
        }
        // Recoverable: prompt for a role and retry. The optimistic row was
        // already rolled back; the retry mints a NEW temp id and re-claims, so
        // release this attempt's claim only if the user abandons the pick.
        setRolePrompt({
          candidateRoleIds: candidates,
          onPick: (roleId) => {
            setRolePrompt(null);
            createEntry({ ...input, customerTeamServiceRoleId: roleId }, opts);
          },
          onCancel: () => {
            setRolePrompt(null);
            opts?.onError?.(tempId);
          },
        });
      },
    );
  };

  // The real PATCH (+ role disambiguation). Always called with a server id —
  // patchEntry resolves temp ids to real ones before dispatching here.
  const dispatchPatch = (
    id: string,
    patch: PatchTimesheetInput,
    opts?: { onError?: () => void },
  ) => {
    // `mutateAsync` for the same reason as `createEntry`: a row rebind patches
    // every draft at once, and each refusal needs its own retry.
    patchMutation.mutateAsync({ id, patch }).then(
      () => {},
      (err) => {
        const engagements = engagementRequiredCandidates(err);
        if (engagements && engagements.length > 0) {
          // The entry's subject AFTER the patch — the question is about the
          // pair the user is moving it to. The cache holds the rolled-back
          // (pre-patch) row by now.
          const entry = queryClient
            .getQueryData<RangeCache>(rangeKey)
            ?.data.find((e) => e.id === id);
          engagementPrompt.ask({
            candidates: engagements,
            customerId:
              patch.customerId !== undefined
                ? (patch.customerId ?? null)
                : (entry?.customerId ?? null),
            productId:
              patch.productId !== undefined
                ? (patch.productId ?? null)
                : (entry?.productId ?? null),
            onPick: (engagementId) => dispatchPatch(id, { ...patch, engagementId }, opts),
            onCancel: () => opts?.onError?.(),
          });
          return;
        }
        const candidates = roleRequiredCandidates(err);
        if (!candidates || candidates.length === 0) {
          opts?.onError?.();
          return;
        }
        setRolePrompt({
          candidateRoleIds: candidates,
          onPick: (roleId) => {
            setRolePrompt(null);
            dispatchPatch(id, { ...patch, customerTeamServiceRoleId: roleId }, opts);
          },
          onCancel: () => {
            setRolePrompt(null);
            opts?.onError?.();
          },
        });
      },
    );
  };

  const patchEntry = (id: string, patch: PatchTimesheetInput, opts?: { onError?: () => void }) => {
    const pending = pendingCreates.current.get(id);
    if (!pending) {
      dispatchPatch(id, patch, opts);
      return;
    }
    // Editing a row whose create is still in flight. Show the change now (the
    // temp row is what the user is looking at) but hold the PATCH until the
    // create returns a real id — a PATCH on the temp id would 404 and roll back
    // to the pre-edit row. The create's onSuccess overwrites this optimistic
    // merge with the server row; the replayed PATCH then re-applies the edit.
    setRange((data) => data.map((e) => (e.id === id ? mergePatch(e, patch) : e)));
    pending.promise.then(
      (realId) => dispatchPatch(realId, patch, opts),
      // Create failed — its onError already rolled the row back; nothing to do.
      () => {},
    );
  };

  // Delete an entry, resolving a still-optimistic temp id to its real id first
  // (a DELETE on a temp id would 404 and roll back, resurrecting the row).
  // `opts.onSuccess` fires only once the DELETE actually lands — callers (the
  // week grid) release their claim there rather than eagerly, so a FAILED delete
  // (which restores the row via onError) leaves the claim intact and the entry
  // stays on its manual line instead of merging back.
  // `opts.onSuccess`/`opts.onError` run per DELETE. We dispatch via `mutateAsync`
  // (not `mutate`) on purpose: several callers fire deletes in a loop against
  // this one shared mutation observer (batch "Clear empty entries", the trash
  // button dropping a multi-draft row). React Query keeps only the LATEST
  // `mutate(_, callbacks)` call's per-call callbacks on the observer, so with
  // `mutate` every delete but the last would silently skip its `onSuccess`
  // (dropClaimedId, batch counting). `mutateAsync` returns a promise scoped to
  // THAT invocation, so each delete resolves its own callback. `opts.onError` is
  // additive — the mutation's config onError (revert + "Delete failed" toast)
  // still fires per delete regardless; this just also lets a caller learn a
  // delete settled as a failure (e.g. a batch counting how many of N landed).
  const deleteEntry = (id: string, opts?: { onSuccess?: () => void; onError?: () => void }) => {
    const run = (realId: string) =>
      deleteMutation.mutateAsync(realId).then(
        () => opts?.onSuccess?.(),
        // Swallow the rejection here (the config onError already reverted +
        // toasted); surfacing it to the caller as a settled-failure is enough.
        () => opts?.onError?.(),
      );
    const pending = pendingCreates.current.get(id);
    if (!pending) {
      void run(id);
      return;
    }
    // Drop it from view now; issue the DELETE once the real id is known.
    setRange((data) => data.filter((e) => e.id !== id));
    pending.promise.then(
      (realId) => void run(realId),
      // The create never landed a real id, so there's nothing to DELETE — but
      // the row is already gone from view, so treat it as settled-failed for a
      // batch caller rather than leaving its counter hanging.
      () => opts?.onError?.(),
    );
  };

  // Both ask-and-retry dialogs; the week grid and the day list render this once.
  const promptHost = (
    <>
      <RoleDisambiguationDialog
        open={rolePrompt != null}
        candidateRoleIds={rolePrompt?.candidateRoleIds ?? []}
        onPick={(roleId) => rolePrompt?.onPick(roleId)}
        onCancel={() => (rolePrompt ? rolePrompt.onCancel() : setRolePrompt(null))}
      />
      {engagementPrompt.host}
    </>
  );

  // Submission lives only on the week view (page-level inline mutation in
  // routes/_authenticated/timesheets.tsx). The day view is a draft-entry
  // surface and intentionally has no submit affordance — keeps the
  // "one transaction for the whole week" promise enforceable in the UI.
  return {
    rangeKey,
    createMutation,
    patchMutation,
    deleteMutation,
    recallEntryMutation,
    createEntry,
    patchEntry,
    deleteEntry,
    /** Recall a single submitted entry back to draft (per-entry, not week-level). */
    recallEntry: (id: string) => recallEntryMutation.mutate(id),
    promptHost,
  };
}
